/**
 * Dolby Vision XML Checker — web version of DolbyVisionChecker.py
 *
 * Produces the same report as the original Python implementation, including
 * the "Disney Spec Checks" and the downloadable .txt report.
 */
(function (global) {
  'use strict';

  // ------------------------------------------------------------------
  // XML parsing (namespace-agnostic, matched by local tag name)
  // ------------------------------------------------------------------

  let parseXmlString = function (text) {
    const doc = new DOMParser().parseFromString(String(text), 'application/xml');
    const errors = doc.getElementsByTagName('parsererror');
    if (errors.length > 0) {
      const first = (errors[0].textContent || 'not well-formed').trim().split('\n')[0];
      throw new Error('XML parse error: ' + first);
    }
    return doc.documentElement;
  };

  function localName(el) {
    if (!el) return '';
    if (el.localName) return el.localName;
    return String(el.tagName || el.nodeName || '').replace(/^[^:]*:/, '');
  }

  function childrenOf(el) {
    if (!el || !el.children) return [];
    return Array.prototype.slice.call(el.children);
  }

  /** First descendant (or self) with the given local name, in document order. */
  function findDesc(root, name) {
    if (!root) return null;
    if (localName(root) === name) return root;
    const kids = childrenOf(root);
    for (let i = 0; i < kids.length; i++) {
      const found = findDesc(kids[i], name);
      if (found) return found;
    }
    return null;
  }

  /** All descendants (incl. self) with the given local name, in document order. */
  function findAllDesc(root, name) {
    const out = [];
    (function walk(el) {
      if (el && localName(el) === name) out.push(el);
      const kids = childrenOf(el);
      for (let i = 0; i < kids.length; i++) walk(kids[i]);
    })(root);
    return out;
  }

  function findChild(parent, name) {
    const kids = childrenOf(parent);
    for (let i = 0; i < kids.length; i++) {
      if (localName(kids[i]) === name) return kids[i];
    }
    return null;
  }

  function findChildren(parent, name) {
    return childrenOf(parent).filter(function (c) { return localName(c) === name; });
  }

  function attr(el, name) {
    if (!el || typeof el.getAttribute !== 'function') return null;
    return el.getAttribute(name);
  }

  function textOf(el) {
    if (!el) return null;
    const t = (el.textContent != null ? String(el.textContent) : '').trim();
    return t || null;
  }

  // ------------------------------------------------------------------
  // Numeric helpers (mirroring the Python)
  // ------------------------------------------------------------------

  const P3_PRIMARIES = { Red: [0.68, 0.32], Green: [0.265, 0.69], Blue: [0.15, 0.06] };
  const REC2020_PRIMARIES = { Red: [0.708, 0.292], Green: [0.17, 0.797], Blue: [0.131, 0.046] };
  const WHITEPOINT_D65 = [0.3127, 0.329];

  const APPROVED_MDP_PROFILES = [
    { name: '1000 nit P3', primaries: P3_PRIMARIES, peakBrightness: 1000 },
    { name: '2000 nit P3', primaries: P3_PRIMARIES, peakBrightness: 2000 },
    { name: '4000 nit P3', primaries: P3_PRIMARIES, peakBrightness: 4000 },
    { name: '1000 nit Rec.2020', primaries: REC2020_PRIMARIES, peakBrightness: 1000 },
    { name: '2000 nit Rec.2020', primaries: REC2020_PRIMARIES, peakBrightness: 2000 },
    { name: '4000 nit Rec.2020', primaries: REC2020_PRIMARIES, peakBrightness: 4000 },
  ];

  function parseFloatStrict(text) {
    if (text === null || text === undefined) return null;
    const n = parseFloat(String(text).trim());
    return Number.isNaN(n) ? null : n;
  }

  function parseXY(text) {
    if (text === null || text === undefined) return null;
    const parts = String(text).replace(/,/g, ' ').trim().split(/\s+/).filter(Boolean);
    if (parts.length < 2) return null;
    const x = parseFloat(parts[0]);
    const y = parseFloat(parts[1]);
    if (Number.isNaN(x) || Number.isNaN(y)) return null;
    return [x, y];
  }

  function matchPrimaries(primaries, standard, tolerance) {
    const colors = ['Red', 'Green', 'Blue'];
    for (let i = 0; i < 3; i++) {
      const p = primaries[colors[i]];
      const s = standard[colors[i]];
      if (!p || !s) return false;
      if (Math.abs(p[0] - s[0]) > tolerance || Math.abs(p[1] - s[1]) > tolerance) return false;
    }
    return true;
  }

  function describePrimaries(primaries) {
    if (matchPrimaries(primaries, P3_PRIMARIES, 0.01)) {
      return 'P3 (x y values: Red 0.680 0.320, Green 0.265 0.690, Blue 0.150 0.060)';
    }
    if (matchPrimaries(primaries, REC2020_PRIMARIES, 0.01)) {
      return 'Rec.2020 (x y values: Red 0.708 0.292, Green 0.170 0.797, Blue 0.131 0.046)';
    }
    return 'Custom primaries';
  }

  function aspectRatioMatches(valueText, referenceText, tolerance) {
    const value = parseFloatStrict(valueText);
    const reference = parseFloatStrict(referenceText);
    if (value === null || reference === null) return false;
    return Math.abs(value - reference) <= (tolerance === undefined ? 0.01 : tolerance);
  }

  function normalizeAspectRatio(valueText, tolerance) {
    const value = parseFloatStrict(valueText);
    if (value === null) return null;
    const t = tolerance === undefined ? 0.01 : tolerance;
    return (Math.round(value / t) * t).toFixed(5);
  }

  /** Mimic Python's str(float) so Level-6 warnings read the same. */
  function pyFloatStr(n) {
    if (Number.isInteger(n)) return n + '.0';
    return String(n);
  }

  function decimalsOf(text) {
    const parts = String(text).split('.');
    return parts.length > 1 ? parts[1].length : 0;
  }

  // ------------------------------------------------------------------
  // Document summary extraction
  // ------------------------------------------------------------------

  function getVersion(root) {
    const versionEl = findDesc(root, 'Version');
    if (versionEl && textOf(versionEl) !== null) return textOf(versionEl);
    const v = attr(root, 'version');
    return v || 'Unknown';
  }

  /** Mirror Python: .//Video/Track/EditRate, else .//Video/Track/Rate n/d. */
  function getFrameRate(root) {
    const videos = findAllDesc(root, 'Video');
    let i;
    for (i = 0; i < videos.length; i++) {
      const tracks = findChildren(videos[i], 'Track');
      for (let j = 0; j < tracks.length; j++) {
        const editRate = findChild(tracks[j], 'EditRate');
        const text = editRate ? textOf(editRate) : null;
        if (text) {
          const parts = text.split(/\s+/).filter(Boolean);
          if (parts.length === 2) {
            const num = parseFloat(parts[0]);
            const den = parseFloat(parts[1]);
            if (!Number.isNaN(num) && !Number.isNaN(den) && den !== 0) return num / den;
          }
        }
      }
    }
    for (i = 0; i < videos.length; i++) {
      const tracks = findChildren(videos[i], 'Track');
      for (let j = 0; j < tracks.length; j++) {
        const rate = findChild(tracks[j], 'Rate');
        if (rate) {
          const n = findChild(rate, 'n');
          const d = findChild(rate, 'd');
          if (n && d && textOf(n) !== null && textOf(d) !== null) {
            const num = parseFloat(textOf(n));
            const den = parseFloat(textOf(d));
            if (!Number.isNaN(num) && !Number.isNaN(den) && den !== 0) return num / den;
          }
        }
      }
    }
    return null;
  }

  /** Shots that are children of Video > Track (matches Python's shot_xpath). */
  function allShots(root) {
    const out = [];
    const videos = findAllDesc(root, 'Video');
    for (let i = 0; i < videos.length; i++) {
      const tracks = findChildren(videos[i], 'Track');
      for (let j = 0; j < tracks.length; j++) {
        out.push.apply(out, findChildren(tracks[j], 'Shot'));
      }
    }
    return out;
  }

  function countShots(root) {
    return allShots(root).length;
  }

  function totalDurationFrames(root) {
    let total = 0;
    const shots = allShots(root);
    for (let i = 0; i < shots.length; i++) {
      const record = findChild(shots[i], 'Record');
      if (!record) continue;
      const duration = findChild(record, 'Duration');
      const text = duration ? textOf(duration) : null;
      if (text === null) continue;
      const n = parseInt(text, 10);
      if (!Number.isNaN(n)) total += n;
    }
    return total;
  }

  function firstShot(root) {
    const shots = allShots(root);
    return shots.length ? shots[0] : null;
  }

  // ------------------------------------------------------------------
  // Section analysis (ColorEncoding / MasteringDisplay)
  // ------------------------------------------------------------------

  function analyzeSection(section) {
    const rows = [];
    const kids = childrenOf(section);
    for (let i = 0; i < kids.length; i++) {
      const child = kids[i];
      const tag = localName(child);
      if (tag.indexOf('Primaries') !== -1) {
        const values = [];
        const map = {};
        const subs = childrenOf(child);
        for (let j = 0; j < subs.length; j++) {
          const sub = subs[j];
          const st = localName(sub);
          const coords = parseXY(textOf(sub));
          if (coords) {
            map[st] = coords;
            values.push({ name: st, x: coords[0], y: coords[1] });
          } else {
            values.push({ name: st, text: textOf(sub) });
          }
        }
        rows.push({ kind: 'primaries', label: describePrimaries(map), values: values });
      } else if (tag === 'WhitePoint') {
        const coords = parseXY(textOf(child));
        const isD65 = coords !== null &&
          Math.abs(coords[0] - WHITEPOINT_D65[0]) < 0.001 &&
          Math.abs(coords[1] - WHITEPOINT_D65[1]) < 0.001;
        rows.push({ kind: 'whitepoint', name: tag, coords: coords, isD65: isD65, raw: textOf(child) });
      } else {
        rows.push({ kind: 'field', name: tag, value: textOf(child) });
      }
    }
    return rows;
  }

  function sectionTextLines(rows) {
    const lines = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row.kind === 'primaries') {
        lines.push('  Primaries:');
        for (let j = 0; j < row.values.length; j++) {
          const v = row.values[j];
          if (v.x !== undefined) {
            lines.push('    ' + v.name + ': x=' + v.x.toFixed(4) + ', y=' + v.y.toFixed(4));
          } else {
            lines.push('    ' + v.name + ': ' + (v.text === null ? 'None' : v.text));
          }
        }
        lines.push('    Description: ' + row.label);
      } else if (row.kind === 'whitepoint') {
        if (row.coords) {
          lines.push('  ' + row.name + ': x=' + row.coords[0].toFixed(4) + ', y=' + row.coords[1].toFixed(4) + (row.isD65 ? ' (D65)' : ''));
        } else {
          lines.push('  ' + row.name + ': ' + (row.raw === null ? 'None' : row.raw));
        }
      } else {
        lines.push('  ' + row.name + ': ' + (row.value === null ? 'None' : row.value));
      }
    }
    return lines;
  }

  // ------------------------------------------------------------------
  // Disney spec checks
  // ------------------------------------------------------------------

  function checkAspectRatioPrecision(root) {
    const canvasAr = findDesc(root, 'CanvasAspectRatio');
    const imageAr = findDesc(root, 'ImageAspectRatio');
    if (canvasAr && imageAr) {
      const cText = textOf(canvasAr);
      const iText = textOf(imageAr);
      const cAr = parseFloatStrict(cText);
      const iAr = parseFloatStrict(iText);
      if (cAr === null || iAr === null) return 'ERROR: Invalid aspect ratio values';
      const cDecimals = decimalsOf(cText);
      const iDecimals = decimalsOf(iText);
      if (cDecimals >= 5 && iDecimals >= 5) return 'PASS: Aspect ratios have 5+ decimal places';
      if (cDecimals >= 2 && iDecimals >= 2) return 'PASS: Aspect ratios have at least 2 decimal places';
      return 'WARNING: Aspect ratios have fewer than 2 decimal places';
    }
    return 'WARNING: Aspect ratios not found';
  }

  function checkStartFrame(root) {
    const shot = firstShot(root);
    if (shot) {
      const record = findChild(shot, 'Record');
      if (record) {
        const inElem = findChild(record, 'In');
        if (inElem) {
          const v = textOf(inElem);
          if (v === '0') return 'PASS: Start frame is 0';
          return 'FAIL: Start frame is ' + v;
        }
      }
    }
    return 'WARNING: Start frame not found';
  }

  function getLevel5Nodes(root) {
    const nodes = [];
    const edr = findAllDesc(root, 'DolbyEDR');
    for (let i = 0; i < edr.length; i++) if (attr(edr[i], 'level') === '5') nodes.push(edr[i]);
    const l5 = findAllDesc(root, 'Level5');
    for (let i = 0; i < l5.length; i++) if (attr(l5[i], 'level') === '5') nodes.push(l5[i]);
    return nodes;
  }

  function checkLevel5Metadata(root) {
    const level5Nodes = getLevel5Nodes(root);
    if (!level5Nodes.length) return 'Level 5 metadata not present';

    const globalCanvas = findDesc(root, 'CanvasAspectRatio');
    const globalImage = findDesc(root, 'ImageAspectRatio');

    let shotCount = 0;
    const allShotsList = findAllDesc(root, 'Shot');
    for (let s = 0; s < allShotsList.length; s++) {
      let hasLevel5 = false;
      const shotEdr = findAllDesc(allShotsList[s], 'DolbyEDR');
      for (let i = 0; i < shotEdr.length; i++) {
        if (attr(shotEdr[i], 'level') === '5') { hasLevel5 = true; break; }
      }
      if (!hasLevel5) {
        const shotL5 = findAllDesc(allShotsList[s], 'Level5');
        for (let i = 0; i < shotL5.length; i++) {
          if (attr(shotL5[i], 'level') === '5') { hasLevel5 = true; break; }
        }
      }
      if (hasLevel5) shotCount += 1;
    }

    const groups = new Map();
    const invalidEntries = [];

    for (let n = 0; n < level5Nodes.length; n++) {
      const idx = n + 1;
      const level5 = level5Nodes[n];
      const aspectRatiosElem = findChild(level5, 'AspectRatios');
      const aspectRatiosText = aspectRatiosElem ? textOf(aspectRatiosElem) : null;
      if (aspectRatiosText === null) {
        invalidEntries.push('Entry ' + idx + ': AspectRatios not found');
        continue;
      }
      const ratios = aspectRatiosText.split(/[\s,]+/).filter(Boolean);

      if (ratios.length === 2) {
        const l5Canvas = ratios[0].trim();
        const l5Image = ratios[1].trim();
        const issues = [];
        if (globalCanvas && !aspectRatioMatches(l5Canvas, textOf(globalCanvas))) {
          issues.push('L5 first AR ' + l5Canvas + ' does not match global CanvasAspectRatio ' + textOf(globalCanvas));
        }
        if (globalImage && aspectRatioMatches(l5Image, textOf(globalImage))) {
          issues.push('L5 second AR ' + l5Image + ' should differ from global ImageAspectRatio ' + textOf(globalImage));
        }
        const normalizedCanvas = normalizeAspectRatio(l5Canvas);
        const normalizedImage = normalizeAspectRatio(l5Image);
        if (normalizedCanvas === null || normalizedImage === null) {
          invalidEntries.push('Entry ' + idx + ': AspectRatios=' + aspectRatiosText + '; invalid numeric values');
          continue;
        }
        const key = normalizedCanvas + '\u0000' + normalizedImage;
        let g = groups.get(key);
        if (!g) {
          g = { count: 0, issues: [], status: 'PASS' };
          groups.set(key, g);
        }
        g.count += 1;
        if (issues.length) {
          g.status = 'FAIL';
          for (let k = 0; k < issues.length; k++) {
            if (g.issues.indexOf(issues[k]) === -1) g.issues.push(issues[k]);
          }
        }
      } else {
        invalidEntries.push('Entry ' + idx + ': AspectRatios format unexpected (' + aspectRatiosText + ')');
      }
    }

    if (!groups.size && !invalidEntries.length) {
      return 'Level 5 metadata present (' + level5Nodes.length + ' entries in ' + shotCount + ' shots); no valid AspectRatios found';
    }

    let passGroups = 0;
    let failGroups = 0;
    groups.forEach(function (g) {
      if (g.status === 'PASS') passGroups += 1;
      else failGroups += 1;
    });

    const reportLines = [
      'Level 5 metadata present (' + level5Nodes.length + ' entries in ' + shotCount + ' shots)',
      '    Group summary: ' + groups.size + ' groups (' + passGroups + ' PASS, ' + failGroups + ' FAIL)' + (invalidEntries.length ? ' ; ' + invalidEntries.length + ' invalid entries' : '')
    ];

    const sorted = Array.from(groups.entries()).sort(function (a, b) {
      const ka = a[0].split('\u0000');
      const kb = b[0].split('\u0000');
      if (ka[0] !== kb[0]) return ka[0] < kb[0] ? -1 : 1;
      if (ka[1] !== kb[1]) return ka[1] < kb[1] ? -1 : 1;
      return 0;
    });

    for (let gi = 0; gi < sorted.length; gi++) {
      const keyParts = sorted[gi][0].split('\u0000');
      const info = sorted[gi][1];
      reportLines.push('    Group ' + (gi + 1) + ': AspectRatios=' + keyParts[0] + ' ' + keyParts[1] + ' ' + info.status + ' (' + info.count + ' entries)');
      if (info.issues.length) {
        info.issues.sort().forEach(function (issue) {
          reportLines.push('      ' + issue);
        });
      }
    }
    invalidEntries.forEach(function (line) { reportLines.push('    ' + line); });
    return reportLines.join('\n');
  }

  function checkMasteringDisplayProfile(root) {
    const masteringDisplay = findDesc(root, 'MasteringDisplay');
    if (!masteringDisplay) return 'FAIL: Mastering Display not found';

    let primariesElem = null;
    const mdKids = childrenOf(masteringDisplay);
    for (let i = 0; i < mdKids.length; i++) {
      if (localName(mdKids[i]).indexOf('Primaries') !== -1) { primariesElem = mdKids[i]; break; }
    }
    if (!primariesElem) return 'FAIL: Primaries not found in Mastering Display';

    const primaries = {};
    const pSubs = childrenOf(primariesElem);
    for (let i = 0; i < pSubs.length; i++) {
      const coords = parseXY(textOf(pSubs[i]));
      if (coords) primaries[localName(pSubs[i])] = coords;
    }

    const wpElem = findChild(masteringDisplay, 'WhitePoint');
    const wpCoords = wpElem ? parseXY(textOf(wpElem)) : null;

    const pbElem = findChild(masteringDisplay, 'PeakBrightness');
    const pbText = pbElem ? textOf(pbElem) : null;
    const pb = pbText === null ? null : parseFloatStrict(pbText);

    if (!wpCoords ||
        Math.abs(wpCoords[0] - WHITEPOINT_D65[0]) > 0.001 ||
        Math.abs(wpCoords[1] - WHITEPOINT_D65[1]) > 0.001) {
      return 'FAIL: White point not D65';
    }
    if (pb === null) return 'FAIL: PeakBrightness not found';

    const idElem = findChild(masteringDisplay, 'ID');
    const idText = (idElem && textOf(idElem) !== null) ? textOf(idElem).trim() : null;

    for (let p = 0; p < APPROVED_MDP_PROFILES.length; p++) {
      const profile = APPROVED_MDP_PROFILES[p];
      if (matchPrimaries(primaries, profile.primaries, 0.001) && Math.abs(pb - profile.peakBrightness) < 1) {
        return idText ? 'PASS: Matches ' + profile.name + ' profile (ID ' + idText + ')' : 'PASS: Matches ' + profile.name + ' profile';
      }
    }
    return 'FAIL: Primaries do not match any approved profile';
  }

  function checkTargetDisplayColorSpace(root) {
    const masteringDisplay = findDesc(root, 'MasteringDisplay');
    if (!masteringDisplay) return 'FAIL: Mastering Display not found for TargetDisplay check';

    let mdpPrimaries = {};
    const mdKids = childrenOf(masteringDisplay);
    for (let i = 0; i < mdKids.length; i++) {
      if (localName(mdKids[i]).indexOf('Primaries') !== -1) {
        const subs = childrenOf(mdKids[i]);
        for (let j = 0; j < subs.length; j++) {
          const coords = parseXY(textOf(subs[j]));
          if (coords) mdpPrimaries[localName(subs[j])] = coords;
        }
        break;
      }
    }
    if (!Object.keys(mdpPrimaries).length) return 'FAIL: Mastering Display primaries not found';

    const mdpDesc = describePrimaries(mdpPrimaries);
    const targetDisplays = findAllDesc(root, 'TargetDisplay');
    const mismatches = [];
    let relevantFound = false;

    for (let t = 0; t < targetDisplays.length; t++) {
      const td = targetDisplays[t];
      const peakElem = findChild(td, 'PeakBrightness');
      const peakText = peakElem ? textOf(peakElem) : null;
      if (peakText === null) continue;
      const peakValue = parseFloatStrict(peakText);
      if (peakValue === null) continue;
      if (peakValue !== 300 && peakValue !== 600 && peakValue !== 1000) continue;

      relevantFound = true;
      const tdIdElem = findChild(td, 'ID');
      const tdIdText = (tdIdElem && textOf(tdIdElem) !== null) ? textOf(tdIdElem).trim() : 'unknown';

      const tdPrimariesElem = findChild(td, 'Primaries');
      if (!tdPrimariesElem) {
        mismatches.push('ID ' + tdIdText + ' (' + Math.round(peakValue) + '-nit): missing primaries');
        continue;
      }
      const tdPrimaries = {};
      const tdSubs = childrenOf(tdPrimariesElem);
      for (let j = 0; j < tdSubs.length; j++) {
        const coords = parseXY(textOf(tdSubs[j]));
        if (coords) tdPrimaries[localName(tdSubs[j])] = coords;
      }
      if (!Object.keys(tdPrimaries).length) {
        mismatches.push('ID ' + tdIdText + ' (' + Math.round(peakValue) + '-nit): missing primaries');
        continue;
      }
      const tdDesc = describePrimaries(tdPrimaries);
      if (!matchPrimaries(tdPrimaries, mdpPrimaries, 0.001)) {
        mismatches.push('ID ' + tdIdText + ' (' + Math.round(peakValue) + '-nit): target ' + tdDesc + ' vs MDP ' + mdpDesc);
      }
    }

    if (!relevantFound) return 'PASS: No 300/600/1000 nit TargetDisplay entries present';
    if (mismatches.length) return 'FAIL: TargetDisplay primaries mismatch for ' + mismatches.join('; ');
    return 'PASS: TargetDisplay primaries match MDP for 300/600/1000 nit entries (MDP: ' + mdpDesc + ')';
  }

  function checkLevel6Metadata(root) {
    const level6 = findDesc(root, 'Level6');
    if (!level6) return 'WARNING: Level 6 metadata not present';

    const maxCllElem = findChild(level6, 'MaxCLL');
    const maxFallElem = findChild(level6, 'MaxFALL');
    const cllText = maxCllElem ? textOf(maxCllElem) : null;
    const fallText = maxFallElem ? textOf(maxFallElem) : null;
    const cllParsed = cllText === null ? 0 : parseFloatStrict(cllText);
    const fallParsed = fallText === null ? 0 : parseFloatStrict(fallText);
    const cll = cllParsed === null ? 0 : cllParsed;
    const fall = fallParsed === null ? 0 : fallParsed;

    if (cll === 0 && fall === 0) return 'PASS: Level 6 MaxCLL and MaxFALL are 0';
    return 'WARNING: Level 6 MaxCLL=' + pyFloatStr(cll) + ', MaxFALL=' + pyFloatStr(fall) + ' (should be 0)';
  }

  // ------------------------------------------------------------------
  // Report assembly
  // ------------------------------------------------------------------

  function buildTextReport(root, base) {
    const lines = [];
    lines.push('Analyzed File: ' + base);
    lines.push('');
    lines.push('Dolby Vision XML Version: ' + getVersion(root));
    lines.push('');

    const canvasAr = findDesc(root, 'CanvasAspectRatio');
    if (canvasAr) lines.push('Canvas Aspect Ratio: ' + textOf(canvasAr));
    const imageAr = findDesc(root, 'ImageAspectRatio');
    if (imageAr) lines.push('Image Aspect Ratio: ' + textOf(imageAr));

    const frameRate = getFrameRate(root);
    if (frameRate !== null) lines.push('Frame Rate: ' + frameRate.toFixed(3) + ' fps');
    lines.push('Shots: ' + countShots(root));
    lines.push('Total Duration (frames): ' + totalDurationFrames(root));
    lines.push('');

    const colorEncoding = findDesc(root, 'ColorEncoding');
    if (colorEncoding) {
      lines.push('Color Encoding:');
      lines.push.apply(lines, sectionTextLines(analyzeSection(colorEncoding)));
    } else {
      lines.push('ColorEncoding not found');
    }

    const masteringDisplay = findDesc(root, 'MasteringDisplay');
    if (masteringDisplay) {
      lines.push('');
      lines.push('Mastering Display:');
      lines.push.apply(lines, sectionTextLines(analyzeSection(masteringDisplay)));
    } else {
      lines.push('MasteringDisplay not found');
    }

    lines.push('');
    lines.push('');
    lines.push('Disney Spec Checks:');
    lines.push('  Aspect Ratio Precision: ' + checkAspectRatioPrecision(root));
    lines.push('  Start Frame: ' + checkStartFrame(root));
    lines.push('  Level 5 Metadata: ' + checkLevel5Metadata(root));
    lines.push('  Mastering Display Profile: ' + checkMasteringDisplayProfile(root));
    lines.push('  TargetDisplay ColorSpace: ' + checkTargetDisplayColorSpace(root));
    lines.push('  Level 6 Metadata: ' + checkLevel6Metadata(root));
    lines.push('');

    return lines.join('\n') + '\n';
  }

  function analyzeDocument(root, fileName) {
    const base = String(fileName || '').split(/[\\/]/).pop() || 'document';
    const sections = [];
    const colorEncoding = findDesc(root, 'ColorEncoding');
    if (colorEncoding) sections.push({ title: 'Color Encoding', rows: analyzeSection(colorEncoding) });
    const masteringDisplay = findDesc(root, 'MasteringDisplay');
    if (masteringDisplay) sections.push({ title: 'Mastering Display', rows: analyzeSection(masteringDisplay) });

    const canvasAr = findDesc(root, 'CanvasAspectRatio');
    const imageAr = findDesc(root, 'ImageAspectRatio');
    const frameRate = getFrameRate(root);

    return {
      ok: true,
      fileName: base,
      summary: {
        fileName: base,
        version: getVersion(root),
        canvasAr: canvasAr ? textOf(canvasAr) : null,
        imageAr: imageAr ? textOf(imageAr) : null,
        frameRate: frameRate,
        shots: countShots(root),
        totalDurationFrames: totalDurationFrames(root),
      },
      sections: sections,
      checks: [
        { name: 'Aspect Ratio Precision', detail: checkAspectRatioPrecision(root) },
        { name: 'Start Frame', detail: checkStartFrame(root) },
        { name: 'Level 5 Metadata', detail: checkLevel5Metadata(root) },
        { name: 'Mastering Display Profile', detail: checkMasteringDisplayProfile(root) },
        { name: 'TargetDisplay ColorSpace', detail: checkTargetDisplayColorSpace(root) },
        { name: 'Level 6 Metadata', detail: checkLevel6Metadata(root) },
      ],
      textReport: buildTextReport(root, base),
    };
  }

  function parseDolbyVisionXml(text, fileName) {
    const root = parseXmlString(text);
    if (!root) throw new Error('Could not parse XML: no root element found.');
    return analyzeDocument(root, fileName);
  }

  // ------------------------------------------------------------------
  // UI helpers
  // ------------------------------------------------------------------

  function escapeHtml(value) {
    return String(value === null || value === undefined ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function statusOf(detail) {
    const m = String(detail).match(/^(PASS|FAIL|WARNING|ERROR):/);
    return m ? m[1].toLowerCase() : 'info';
  }

  function statusLabel(status) {
    if (status === 'pass') return 'PASS';
    if (status === 'fail') return 'FAIL';
    if (status === 'warn') return 'WARNING';
    if (status === 'error') return 'ERROR';
    return 'INFO';
  }

  function kvRow(name, value, extraClass) {
    const cls = extraClass ? 'kv-row ' + extraClass : 'kv-row';
    return '<div class="' + cls + '"><span class="kv-name">' + escapeHtml(name) + '</span><span class="kv-value">' + escapeHtml(value) + '</span></div>';
  }

  function renderSummary(summary) {
    const html = ['<section class="report-block"><h3>Summary</h3><div class="kv-grid kv-grid--3">'];
    html.push(kvRow('File', summary.fileName, 'kv-row--wide'));
    html.push(kvRow('Dolby Vision XML Version', summary.version));
    html.push(kvRow('Canvas Aspect Ratio', summary.canvasAr));
    html.push(kvRow('Image Aspect Ratio', summary.imageAr));
    html.push(kvRow('Frame Rate', summary.frameRate === null ? 'Not found' : summary.frameRate.toFixed(3) + ' fps'));
    html.push(kvRow('Shots', String(summary.shots)));
    html.push(kvRow('Total Duration (frames)', String(summary.totalDurationFrames)));
    html.push('</div></section>');
    return html.join('');
  }

  function renderSection(section, opts) {
    const gridClass = (opts && opts.grid) ? 'kv-grid ' + opts.grid : 'kv-grid';
    const html = ['<section class="report-block"><h3>' + escapeHtml(section.title) + '</h3><div class="' + gridClass + '">'];
    for (let i = 0; i < section.rows.length; i++) {
      const row = section.rows[i];
      if (row.kind === 'primaries') {
        html.push('<div class="primaries-block"><span class="kv-name">Primaries</span><div class="primaries-grid">');
        for (let j = 0; j < row.values.length; j++) {
          const v = row.values[j];
          const xy = v.x !== undefined ? 'x=' + v.x.toFixed(4) + ', y=' + v.y.toFixed(4) : (v.text === null ? 'None' : String(v.text));
          html.push('<div class="prim"><span class="prim-name">' + escapeHtml(v.name) + '</span><span class="prim-xy">' + escapeHtml(xy) + '</span></div>');
        }
        html.push('</div><span class="gamut-chip">' + escapeHtml(row.label) + '</span></div>');
      } else if (row.kind === 'whitepoint') {
        const label = row.coords ? 'x=' + row.coords[0].toFixed(4) + ', y=' + row.coords[1].toFixed(4) + (row.isD65 ? ' · D65' : '') : (row.raw === null ? 'None' : String(row.raw));
        html.push(kvRow(row.name, label));
      } else {
        const extraClass = (opts && opts.wideNames && /name$/i.test(row.name)) ? 'kv-row--rest' : '';
        html.push(kvRow(row.name, row.value === null ? 'None' : String(row.value), extraClass));
      }
    }
    html.push('</div></section>');
    return html.join('');
  }

  function renderChecks(checks) {
    const html = ['<section class="report-block checks-block"><h3>Disney Spec Checks</h3>'];
    for (let i = 0; i < checks.length; i++) {
      const check = checks[i];
      const status = statusOf(check.detail);
      const multiline = String(check.detail).indexOf('\n') !== -1;
      html.push('<div class="check-row"><div class="check-head"><span class="badge ' + status + '">' + statusLabel(status) + '</span><span class="check-name">' + escapeHtml(check.name) + '</span></div>');
      if (multiline) {
        html.push('<div class="check-detail pre">' + escapeHtml(check.detail) + '</div>');
      } else {
        html.push('<p class="check-detail">' + escapeHtml(check.detail) + '</p>');
      }
      html.push('</div>');
    }
    html.push('</section>');
    return html.join('');
  }

  function renderResult(result) {
    const html = [renderSummary(result.summary)];
    for (let i = 0; i < result.sections.length; i++) {
      const section = result.sections[i];
      const opts = section.title === 'Mastering Display' ? { grid: 'kv-grid--2', wideNames: true } : null;
      html.push(renderSection(section, opts));
    }
    html.push(renderChecks(result.checks));
    return html.join('');
  }

  function renderError(message) {
    return '<div class="error-panel"><strong>Analysis failed</strong><p>' + escapeHtml(message) + '</p></div>';
  }

  function downloadReport(filename, text) {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
  }

  // ------------------------------------------------------------------
  // UI wiring
  // ------------------------------------------------------------------

  function initUi() {
    if (typeof document === 'undefined') return false; //SIP-842
    const fileInput = document.getElementById('xml-file');
    const dropZone = document.getElementById('drop-zone');
    const analyzeBtn = document.getElementById('analyze-btn');
    const reportPanel = document.getElementById('report-panel');
    const reportEl = document.getElementById('report');
    const downloadBtn = document.getElementById('download-btn');
    const fileNameEl = document.getElementById('file-name');
    if (!fileInput || !dropZone || !analyzeBtn || !reportPanel || !reportEl || !downloadBtn) return false; //SIP-842

    let currentFile = null;
    let currentTextReport = null;

    function setFile(file) {
      currentFile = file || null;
      if (fileNameEl) {
        fileNameEl.hidden = !file;
        fileNameEl.textContent = file ? file.name : '';
      }
      analyzeBtn.disabled = !file;
      if (file) dropZone.classList.add('has-file');
      else dropZone.classList.remove('has-file');
    }

    fileInput.addEventListener('change', function () {
      setFile(fileInput.files && fileInput.files[0]);
    });

    dropZone.addEventListener('dragover', function (e) { e.preventDefault(); dropZone.classList.add('dragging'); });
    dropZone.addEventListener('dragenter', function (e) { e.preventDefault(); dropZone.classList.add('dragging'); });
    dropZone.addEventListener('dragleave', function (e) { e.preventDefault(); dropZone.classList.remove('dragging'); });
    dropZone.addEventListener('drop', function (e) {
      e.preventDefault();
      dropZone.classList.remove('dragging');
      const files = e.dataTransfer && e.dataTransfer.files;
      if (files && files.length) {
        const file = files[0];
        try {
          const dt = new DataTransfer();
          dt.items.add(file);
          fileInput.files = dt.files;
        } catch (err) {
          // file input may not be writable; state is tracked via currentFile anyway
        }
        setFile(file);
      }
    });
    dropZone.addEventListener('click', function () { fileInput.click(); });
    dropZone.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        fileInput.click();
      }
    });

    function analyze() {
      if (!currentFile) return;
      reportPanel.hidden = false;
      reportEl.innerHTML = '<p class="status-line">Analyzing <strong>' + escapeHtml(currentFile.name) + '</strong>…</p>';
      downloadBtn.disabled = true;
      currentTextReport = null;

      const reader = new FileReader();
      reader.onload = function () {
        try {
          const text = String(reader.result || '');
          const result = parseDolbyVisionXml(text, currentFile.name);
          currentTextReport = result.textReport;
          reportEl.innerHTML = renderResult(result);
          downloadBtn.disabled = false;
        } catch (err) {
          currentTextReport = null;
          reportEl.innerHTML = renderError(err && err.message ? err.message : String(err));
        }
      };
      reader.onerror = function () {
        currentTextReport = null;
        reportEl.innerHTML = renderError('Could not read the file.');
      };
      reader.readAsText(currentFile, 'UTF-8');
    }

    analyzeBtn.addEventListener('click', analyze);

    downloadBtn.addEventListener('click', function () {
      if (currentTextReport === null || !currentFile) return;
      const base = currentFile.name.replace(/\.[^.]+$/, '') || 'report';
      downloadReport(base + '_report.txt', currentTextReport);
    });

    return true; //SIP-842
  }

  // ------------------------------------------------------------------
  // Embed boot (hybrid embed: local bundle, no S3)
  // ------------------------------------------------------------------

  //SIP-842
  function resolveEmbedRoot() {
    return (
      document.querySelector('[data-dovi-strapi]') ||
      document.querySelector('[data-nsdr-strapi], [data-nsdr-js]') ||
      document.body
    );
  }

  //SIP-842
  function isSafeToBoot() {
    try {
      const path = String(global.location ? global.location.pathname : '').toLowerCase();
      if (path.indexOf('/admin') >= 0 || path.indexOf('content-manager') >= 0) return false;
      const embed = document.querySelector('[data-dovi-strapi], [data-nsdr-strapi], [data-nsdr-js]');
      if (embed instanceof HTMLElement && embed.closest('[contenteditable="true"], .ck-editor, .ck-editor__editable, .ck-content')) return false;
    } catch (err) {
      return false;
    }
    return true;
  }

  //SIP-842
  function isLocalPreviewPage() {
    return Boolean(
      document.querySelector('#xml-file') &&
      !document.querySelector('[data-dovi-strapi], [data-nsdr-strapi], [data-nsdr-js]')
    );
  }

  //SIP-842
  function isRootBooted(root) {
    return root instanceof HTMLElement && global.__DOVI_BOOTED_ROOTS__ && global.__DOVI_BOOTED_ROOTS__.has(root);
  }

  //SIP-842
  function autoInitEmbeddedUi() {
    if (!isSafeToBoot()) return;
    const root = resolveEmbedRoot();
    if (!(root instanceof HTMLElement)) return;
    if (isRootBooted(root) || global.__DOVI_BOOTING__) return;
    global.__DOVI_BOOTING__ = true;
    if (initUi()) {
      if (!global.__DOVI_BOOTED_ROOTS__) global.__DOVI_BOOTED_ROOTS__ = new WeakSet();
      global.__DOVI_BOOTED_ROOTS__.add(root);
      global.__DOVI_BOOTING__ = false;
    } else {
      global.__DOVI_BOOTING__ = false;
    }
  }

  // ------------------------------------------------------------------
  // Exports
  // ------------------------------------------------------------------

  const api = {
    parseDolbyVisionXml: parseDolbyVisionXml,
    analyzeDocument: analyzeDocument,
    buildTextReport: buildTextReport,
    getFrameRate: getFrameRate,
    countShots: countShots,
    _setXmlParser: function (fn) { parseXmlString = fn; },
    autoInitEmbeddedUi: autoInitEmbeddedUi, //SIP-842
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (global) global.DoviChecker = api;

  //SIP-842: local preview only; embeds boot via autoInitEmbeddedUi()
  if (typeof document !== 'undefined' && isLocalPreviewPage()) {
    const runLocalBoot = function () { autoInitEmbeddedUi(); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', runLocalBoot);
    else runLocalBoot();
  }
})(typeof window !== 'undefined' ? window : globalThis);







